import React, { useContext, useEffect, useState } from "react";
import { Form, Input, Button } from "antd";
import {
  CloseOutlined,
  UserOutlined,
  CheckOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { TResponseModel } from "../../types/config";
import { useNavigate } from "react-router-dom";
import useFetch from "../../hooks/useFetch";
import { eFONTFAMILY, eResultCode } from "../../utils/enum";
import useToast from "../../hooks/useToast";
import { ToastOpen, ToastType } from "../../state/toast/slice";
import styles from "./auth.module.css";
import { UserContext } from "../../context";
import EncryptUtils from "../../utils/encrypt";
import { LoaderContext } from "../../context/loader";

const ForgotPassword: React.FC = () => {
  const context: any = useContext(UserContext);
  const navigate = useNavigate();
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [isOTPSent, setIsOTPSent] = useState<boolean>(false);
  const [isResendMode, setIsResendMode] = useState<boolean>(false);
  const [timer, setTimer] = useState<number>(300);
  const [otp, setOtp] = useState<string>("");
  const [isOTPVerified, setIsOTPVerified] = useState<boolean>(false);
  const [form] = Form.useForm();
  const emailId = context.user?.emailId;

  // Context For Show Or Hide Loader
  const contextForLoader = useContext(LoaderContext);
  // const { isLoading, setIsLoading } = contextForLoader as any;
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (emailId) {
      form.setFieldsValue({ email: emailId });
    }
  }, [emailId, form]);

  const onSendOTP = async (values: { email: string }) => {
    try {
      setIsLoading(true);
      const payload = {
        data: {
          emailId: values.email,
        },
      };
      const response: TResponseModel = await post(
        "/OpenPage/GenerateOtp",
        payload
      );

      const { dataResponse } = response;
      const { returnCode, description } = dataResponse;

      if (returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.leftBottom,
          content: description,
        });
        setIsOTPSent(true);
        setIsResendMode(false);
        setTimer(300);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.leftBottom,
          content: description,
        });
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const onVerifyOTP = async (values: { otp: string }) => {
    try {
      setIsLoading(true);
      const email = form.getFieldValue("email");
      const payload = {
        data: {
          emailId: email,
          otp: values.otp,
        },
      };
      const response: TResponseModel = await post(
        "/OpenPage/ValidateOtp",
        payload
      );

      const { dataResponse, authToken } = response;
      const { returnCode, description } = dataResponse;

      if (response?.dataResponse?.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.leftBottom,
          content: description,
        });
        setIsOTPVerified(true); // Set OTP verified to true
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.leftBottom,
          content: description,
        });
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitNewPassword = async (values: {
    email: string;
    password: string;
    confirmPassword: string;
  }) => {
    if (values.password !== values.confirmPassword) {
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.leftBottom,
        content: "Passwords do not match!",
      });
      return;
    }

    try {
      setIsLoading(true);
      const email = form.getFieldValue("email");
      const encryptedEmail = btoa(email); // Encrypt email to base64
      const password = EncryptUtils.encrypt(values.password as string);
      const payload = {
        data: {
          emailId: encryptedEmail,
          password: password,
        },
      };
      const response: TResponseModel = await post(
        "/OpenPage/ChangePassword",
        payload
      );

      const { dataResponse } = response;
      const { returnCode, description } = dataResponse;

      if (response?.dataResponse?.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.leftBottom,
          content: description,
        });
        navigate("/login"); // Redirect to login after password reset
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.leftBottom,
          content: description,
        });
      }
    } catch (error) {
      console.error("Error submitting new password:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Timer countdown
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else if (timer === 0) {
      setIsResendMode(true); // Enable resend mode
      setOtp("");
      form.resetFields(["otp"]);
    }
  }, [timer, form]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const backToLogin = () => {
    navigate("/login");
  };

  return (
    <div className={styles.parentWrapper}>
      {!isOTPVerified ? (
        !isOTPSent ? (
          <Form
            form={form}
            name="forgotPassword"
            onFinish={onSendOTP}
            size="middle"
            initialValues={{ email: emailId }}
          >
            <div className={styles.formHeader}>
              <span
                style={{ fontFamily: eFONTFAMILY.SEMIBOLD, fontSize: "22px" }}
              >
                Change Password?
              </span>
            </div>
            <p>Please enter your email to receive an OTP</p>
            <Form.Item
              name="email"
              rules={[
                { required: true, message: "Please input your Email!" },
                { type: "email", message: "Please enter a valid Email!" },
              ]}
            >
              <Input prefix={<UserOutlined />} placeholder="Email" />
            </Form.Item>
            <Form.Item>
              <Button
                block
                type="primary"
                htmlType="submit"
                loading={isLoading}
              >
                Send OTP
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <Form name="verifyOTP" onFinish={onVerifyOTP} size="middle">
            <div className={styles.formHeader}>
              <h1>Verify OTP</h1>
            </div>
            <p>Enter the OTP sent to your email address</p>
            <Form.Item
              name="otp"
              rules={[{ required: true, message: "Please input the OTP!" }]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
              />
              {timer === 0 && (
                <p style={{ color: "red", fontSize: "12px" }}>
                  *OTP has expired. Please resend a new OTP.
                </p>
              )}
            </Form.Item>
            <Form.Item>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <p className={styles.timer} style={{ marginTop: "5px" }}>
                  Remaining time :{" "}
                  <span style={{ fontWeight: "600" }}>{formatTime(timer)}</span>
                </p>
                <Button
                  type="link"
                  disabled={!isResendMode}
                  onClick={() =>
                    onSendOTP({ email: form.getFieldValue("email") })
                  }
                >
                  Resend OTP?
                </Button>
              </div>
            </Form.Item>
            <Form.Item>
              <Button
                block
                type="primary"
                htmlType="submit"
                loading={isLoading}
              >
                Verify OTP
              </Button>
            </Form.Item>
          </Form>
        )
      ) : (
        <Form name="newPassword" onFinish={onSubmitNewPassword} size="middle">
          <div className={styles.formHeader}>
            <h1>Set New Password</h1>
          </div>
          <Form.Item
            name="password"
            rules={[
              { required: true, message: "Please input your new password!" },
              {
                pattern:
                  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                message:
                  "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character!",
              },
            ]}
          >
            <Input.Password placeholder="New Password" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            rules={[
              { required: true, message: "Please confirm your new password!" },
              {
                pattern:
                  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                message:
                  "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character!",
              },
            ]}
          >
            <Input.Password placeholder="Confirm New Password" />
          </Form.Item>
          <Form.Item>
            <Button block type="primary" htmlType="submit" loading={isLoading}>
              Submit
            </Button>
          </Form.Item>
        </Form>
      )}
      <Button type="link" onClick={backToLogin}>
        <ArrowLeftOutlined />
        Back
      </Button>
    </div>
  );
};

export default ForgotPassword;
